import { useQueryClient } from '@tanstack/vue-query'
import { APP_ROUTES } from '~/constants'
import type { UserClaims } from '~/types/gql/generated/graphql'
import { VerifyAuthGqlQuery } from '~/queries/auth/verify-auth.query'
import { QUERY_KEYS } from '~/queries/keys'

/**
 * Handle permission level on frontend for pages access only
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
  const runtimeConfig = useRuntimeConfig()
  const isUnderMaintenance = runtimeConfig.public.MAINTENANCE
  const whitelistedUsersEmailsDomains = runtimeConfig.public.WHITELISTED_USERS_EMAILS_DOMAINS
  const { resetLoginAttempts } = useLoginAttempts()
  const { decodedToken, setToken } = useSession()
  const { requester } = useRequester()
  const queryClient = useQueryClient()

  /**
   * Verify token with backend if a decoded token exists
   * This ensures the token is still valid on the server side
   */
  if (decodedToken.value?.payload?.value && to.path !== APP_ROUTES.login.path) {
    try {
      const result = await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.auth, decodedToken.value.payload.value.iat],
        queryFn: async () => requester(VerifyAuthGqlQuery),
      })

      // If backend returns false, token is invalid - clear and redirect to login
      if (!result?.verifyAuth) {
        setToken(null)
        const loginPath = `/login${to.path !== '/' ? ['?redirect=', encodeURIComponent(to.fullPath)].join('') : ''}`
        return navigateTo(loginPath)
      }
      console.debug('Resetting login attempts since verify auth success')
      resetLoginAttempts()
    }
    catch (error) {
      console.error('Error while verify auth', error)
      // Network/server error - abort navigation to stay on current page
      return abortNavigation()
    }
  }

  /**
   * We redirect every route that goes anywhere but the login path
   * if you're not an admin during a maintenance
   */
  if (!decodedToken.value?.payload.value?.isAdmin && isUnderMaintenance.active && to.path !== APP_ROUTES.login.path) {
    if (decodedToken.value?.payload.value) {
      const emailDomain = decodedToken.value.payload.value.email.split('@')[1]
      if (whitelistedUsersEmailsDomains?.includes(emailDomain)) {
        return true
      }
    }
    return navigateTo(APP_ROUTES.login.path)
  }

  const _currentUser = decodedToken.value?.payload?.value

  // should let nuxt auth handle the redirect once this issue is fixed
  // https://github.com/sidebase/nuxt-auth/issues/857
  // if the page requires authentication and the user is not logged in, redirect to login with the target page as redirect
  if ((to.meta?.auth || to.meta?.authorities) && !_currentUser) {
    const loginPath = `/login${to.path !== '/' ? ['?redirect=', encodeURIComponent(to.fullPath)].join('') : ''}`
    return navigateTo(loginPath)
  }

  // check if the page needs specific authorization
  if (to.meta?.authorities) {
    if (_currentUser?.isAdmin)
      return true

    const hasToAuthority = hasAuthority(to.meta.authorities, _currentUser)

    if (hasToAuthority)
      return true

    if (from.meta?.authorities) {
      const hasFromAuthority = hasAuthority(from.meta.authorities, _currentUser)

      if (hasFromAuthority) {
        // if has authority to stay on the current page he was on just block on current page
        return from.path.includes(APP_ROUTES.login.path) ? navigateTo(APP_ROUTES.home.path) : false
      }
      // else redirect to the home page
      return navigateTo(APP_ROUTES.home.path)
    }

    return from.path.includes(APP_ROUTES.login.path) ? navigateTo(APP_ROUTES.home.path) : false
  }

  return true
})

function hasAuthority(authorities: Record<string, boolean>, currentUser: UserClaims): boolean {
  return Object.keys(authorities).some((authority) => {
    const _authority = authority as keyof typeof currentUser
    return currentUser[_authority]
  })
}
